import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HostListItem } from "@/hooks/useClients";

interface TargetsStepProps {
  targets: HostListItem[];
  selectedTargetIds: string[];
  onToggleTarget: (targetId: string) => void;
}

/**
 * Pick the clients (execution targets) for this run. Seeded from the
 * clients enabled on the Clients page; editable per run. Each card shows the
 * model the client is configured to run (set on the Clients page).
 */
export function TargetsStep({
  targets,
  selectedTargetIds,
  onToggleTarget,
}: TargetsStepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg pb-2">Choose clients</h3>
        <p className="text-sm text-muted-foreground">
          Select one or more clients. Each test will run against all selected
          clients, using the model configured for each — and the connectors
          attached to it (the chips below; edit on the Client Connectors page).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {targets.map((target) => {
          const isSelected = selectedTargetIds.includes(target.hostId);
          const missingModel = !target.model;

          return (
            <button
              key={target.hostId}
              type="button"
              onClick={() => onToggleTarget(target.hostId)}
              data-testid={`eval-target-${target.hostId}`}
              className={cn(
                "rounded-xl border p-4 text-left transition-colors relative",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "hover:border-primary/40",
                missingModel && "opacity-60",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm">{target.name}</h4>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {target.model ?? "No model configured"}
                  </p>
                  {/* The connectors this client's runs attach (targets.server_ids —
                      eval runs use each client's own attachment). */}
                  {target.serverIds && target.serverIds.length > 0 ? (
                    <div
                      className="mt-2 flex flex-wrap gap-1"
                      data-testid={`eval-target-connectors-${target.hostId}`}
                    >
                      {target.serverIds.map((name) => (
                        <span
                          key={name}
                          className="rounded-full border bg-muted/60 px-2 py-0.5 text-[10px] leading-4 text-muted-foreground"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-warning-foreground">
                      No connectors attached — runs will only attach the
                      case's org connector
                    </p>
                  )}
                  {!target.enabled && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Disabled on the Clients page
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedTargetIds.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3">
          <p className="text-sm font-medium">
            {selectedTargetIds.length} client
            {selectedTargetIds.length === 1 ? "" : "s"} selected
          </p>
        </div>
      )}
    </div>
  );
}
